import React, { useState, useEffect } from "react";
import { Col, Row, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import personalIconSvg from "../../../assets/images/personalIconSvg.svg";
import { postData } from "../../../api";
import { autocloseTiming } from "../../../api/regex";
import Loader from "../../../components/Loader";

const YES_NO_OPTIONS = ["Yes", "No"];

const sortFields = (fields) =>
  Array.isArray(fields)
    ? [...fields].sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
    : [];

const CustomVolunteersTab = ({ customFieldsData, userid }) => {
  const [fields, setFields] = useState(() => sortFields(customFieldsData));
  const [errors, setErrors] = useState({});
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFields(sortFields(customFieldsData));
    setErrors({});
    setValidated(false);
  }, [customFieldsData]);

  const handleChange = (id, value) => {
    setFields((prev) =>
      prev.map((field) =>
        field.id === id ? { ...field, answer_value: value } : field
      )
    );
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: null }));
    }
  };

  const findFormErrors = () => {
    const newErrors = {};
    fields.forEach((field) => {
      const value = field?.answer_value;
      const isEmpty =
        value === null || value === undefined || String(value).trim() === "";
      if (Number(field?.required) === 1 && isEmpty) {
        newErrors[field.id] =
          field.field_type === "text"
            ? `Please enter ${field.field_name}.`
            : `Please select ${field.field_name}.`;
      }
    });
    return newErrors;
  };

  const saveCustomFields = async () => {
    setLoading(true);
    const data = {
      user_id: userid,
      stage: "custom_fields",
      type: "edit",
      custom_fields: fields.map(({ id, field_type, answer_value }) => ({
        id,
        field_type,
        answer_value: answer_value ?? "",
      })),
    };
    const res = await postData("profile", {}, data);
    if (res?.status === true) {
      toast.success(res.message, {
        theme: "colored",
        autoClose: autocloseTiming,
      });
    } else {
      toast.error(res?.message, {
        theme: "colored",
        autoClose: autocloseTiming,
      });
    }
    setLoading(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = findFormErrors();
    setValidated(true);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      saveCustomFields();
    }
  };

  const renderField = (field) => {
    const value = field?.answer_value ?? "";
    const isRequired = Number(field?.required) === 1;
    const controlId = `custom_field_${field.id}`;

    if (field.field_type === "yes_no" || field.field_type === "dropdown") {
      const options =
        field.field_type === "yes_no"
          ? YES_NO_OPTIONS
          : (field?.options || [])
              .slice()
              .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
              .map((option) => option?.option_label);

      return (
        <Form.Select
          id={controlId}
          name={controlId}
          value={value}
          onChange={(e) => handleChange(field.id, e.target.value)}
          isInvalid={!!errors[field.id]}
          required={isRequired}
          size="lg"
        >
          <option value="">Select</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Select>
      );
    }

    return (
      <Form.Control
        id={controlId}
        name={controlId}
        type="text"
        placeholder={`Enter ${field.field_name}`}
        value={value}
        onChange={(e) => handleChange(field.id, e.target.value)}
        isInvalid={!!errors[field.id]}
        required={isRequired}
      />
    );
  };

  return (
    <>
      {!loading && (
        <div className="custom_volunteers_container">
          <Row className="d-flex justify-content-between mb-3">
            <Col>
              <h5 className="tabs_main_heading">
                <span className="me-1">
                  <img src={personalIconSvg} alt="" />
                </span>
                Additional Field Info
              </h5>
            </Col>
          </Row>
          {fields.length > 0 ? (
            <Form
              noValidate
              validated={validated}
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <Row>
                {fields.map((field) => (
                  <Col lg={4} key={field.id}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor={`custom_field_${field.id}`}>
                        {field.field_name}
                        {Number(field?.required) === 1 && (
                          <span className="asterisk"> *</span>
                        )}
                      </Form.Label>
                      {renderField(field)}
                      <Form.Control.Feedback type="invalid">
                        {errors[field.id]}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                ))}
              </Row>
              <Row className="d-flex justify-content-end">
                <Col sm={12} md={4} className="d-flex justify-content-end">
                  <Button variant="primary" className="save_btn" type="submit">
                    Save
                  </Button>
                </Col>
              </Row>
            </Form>
          ) : (
            <h4 className="no-data mb-0">No data found</h4>
          )}
        </div>
      )}
      {loading && <Loader />}
    </>
  );
};

export default CustomVolunteersTab;
